import { useCallback } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import * as Sentry from '@sentry/nextjs'
import { useDispatch, useSelector } from 'react-redux'

import { NotificationType } from '@/graphql/appsync-notifications'
import { useUpdateMySettingMutation } from '@/graphql/generated/hooks'
import { IncidentPriority } from '@/graphql/generated/schemas'
import {
  selectIncidentAudioAlert,
  selectUserSettingsLoaded,
  setIncidentAudioAlert,
} from '@/redux/ui/uiSlice'
import { trackEvent } from '@/utils/analytics'

import { AUDIO_SETTINGS_ANALYTICS } from '../constants/constants'

// TODO - In future make this customizable
export const getIncidentAlertSound = (
  priority?: IncidentPriority | null
): string => {
  switch (priority) {
    case IncidentPriority.Critical:
      return '/audio/critical_incident_notification.mp3'
    default:
      return '/audio/incident_notification.mp3'
  }
}

export const useIncidentAudio = () => {
  const dispatch = useDispatch()
  const [updateMySetting, { loading: isUpdatingAudioSetting }] =
    useUpdateMySettingMutation()

  const isFeatureFlagOn = useFeatureFlag(FeatureFlag.incidentAudioAlerts)
  const isCriticalBypassOn = useFeatureFlag(FeatureFlag.priorityQueue)

  const isAudioAlertOn: boolean = useSelector(selectIncidentAudioAlert)
  const isUserSettingsLoaded: boolean = useSelector(selectUserSettingsLoaded)

  const toggleAudioEnabled = useCallback(async () => {
    const isAudioAlertOnBeforeUpdate = isAudioAlertOn
    const shouldTurnOn = !isAudioAlertOn

    dispatch(setIncidentAudioAlert(shouldTurnOn))

    try {
      const { data } = await updateMySetting({
        variables: {
          input: {
            audibleAlertsEnabled: shouldTurnOn,
          },
        },
      })

      const isAudibleAlertsEnabled =
        data?.updateMySetting?.userSetting?.audibleAlertsEnabled

      if (isAudibleAlertsEnabled !== undefined) {
        dispatch(setIncidentAudioAlert(isAudibleAlertsEnabled))
      }

      trackEvent(AUDIO_SETTINGS_ANALYTICS.toggleAlerts, {
        isNowOn: shouldTurnOn,
      })
    } catch (e) {
      dispatch(setIncidentAudioAlert(isAudioAlertOnBeforeUpdate))
      Sentry.captureException(e)
    }
  }, [dispatch, isAudioAlertOn, updateMySetting])

  /**
   * If the Setting in on AND the feature flag is on, the sound will attempt
   * to be played. A newly-arrived Critical-priority incident bypasses the
   * per-user setting (but not the feature flag) when the priorityQueue flag
   * is on.
   * @return Bool if the sound was successfully played
   */
  const playAlertSound = useCallback(
    async (
      priority?: IncidentPriority | null,
      notificationType?: NotificationType
    ): Promise<boolean> => {
      // Be sure to also check FF! Failing to do so could result in a state where the FF was on, the setting got
      // turned on, then the FF was removed. Now the user will constantly hear the alerts, without the ability to
      // turn them off
      if (!isFeatureFlagOn) {
        return false
      }

      // Priority belongs to the incident, not the event — restrict the bypass to the
      // incident's arrival so a later clear/flag/audio-message event doesn't also bypass
      // the per-user setting.
      const isCriticalBypass =
        priority === IncidentPriority.Critical &&
        notificationType === NotificationType.IncidentNew &&
        isCriticalBypassOn

      // Non-bypassed alerts need the per-user setting loaded to respect it. A Critical
      // bypass ignores that setting entirely, so it shouldn't wait on it either — otherwise
      // a Critical notification arriving before settings finish loading plays no sound at all.
      if (!isCriticalBypass && (!isUserSettingsLoaded || !isAudioAlertOn)) {
        return false
      }

      const audioFile = new Audio(getIncidentAlertSound(priority))
      try {
        await audioFile.play()
        return true
      } catch (e) {
        // Be sure to ship error up, but don't have this break anything
        Sentry.captureException(e)
      }

      return false
    },
    [isAudioAlertOn, isCriticalBypassOn, isFeatureFlagOn, isUserSettingsLoaded]
  )

  return {
    isEnabled: isAudioAlertOn,
    isUpdatingAudioSetting,
    toggleAudioEnabled,
    playAlertSound,
  }
}
